import {
  collection, addDoc, getDocs, query, where, orderBy,
  serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { Report, ReportType, ReportStatus } from '../types';

export async function requestReport(
  type: ReportType,
  userId: string,
  userEmail: string,
  timeFrom?: Date,
  timeTo?: Date,
  recipients?: string[],
): Promise<string> {
  const ref = await addDoc(collection(db, 'reports'), {
    type,
    requestedBy: userId,
    requestedByEmail: userEmail,
    ...(recipients && recipients.length > 0 ? { recipients } : {}),
    status: 'pending' as ReportStatus,
    createdAt: serverTimestamp(),
    ...(timeFrom ? { timeFrom: Timestamp.fromDate(timeFrom) } : {}),
    ...(timeTo ? { timeTo: Timestamp.fromDate(timeTo) } : {}),
  });
  return ref.id;
}

function mapReportDoc(d: { id: string; data: () => Record<string, unknown> }): Report {
  const data = d.data();
  const toDate = (v: unknown): Date | undefined => {
    if (!v) return undefined;
    if (v instanceof Timestamp) return v.toDate();
    const obj = v as { toDate?: () => Date };
    if (typeof obj.toDate === 'function') return obj.toDate();
    return undefined;
  };
  return {
    id: d.id,
    type: data.type as ReportType,
    requestedBy: data.requestedBy as string,
    requestedByEmail: (data.requestedByEmail as string) ?? '',
    recipients: (data.recipients as string[]) ?? undefined,
    status: data.status as ReportStatus,
    errorMessage: (data.errorMessage as string) ?? undefined,
    createdAt: toDate(data.createdAt) ?? new Date(),
    timeFrom: toDate(data.timeFrom),
    timeTo: toDate(data.timeTo),
    downloadUrl: (data.downloadUrl as string) ?? undefined,
  };
}

export async function getMyReports(userId: string): Promise<Report[]> {
  const q = query(
    collection(db, 'reports'),
    where('requestedBy', '==', userId),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(mapReportDoc);
}

export async function getAllReports(): Promise<Report[]> {
  const q = query(
    collection(db, 'reports'),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map(mapReportDoc);
}
